/**
 * Remembers the layout of deleted ads so re-adding the same size restores it
 * instead of re-deriving it through the layout engine.
 *
 * Deleting a size and adding it back is usually a mistake, and re-deriving
 * would silently discard every per-ad tweak the user had made on that ad.
 * `applyDeleteAd` files a snapshot here; `createAd` takes it back and carries
 * it in the CREATE_AD payload so the apply and redo paths stay identical.
 *
 * Snapshots are keyed by SIZE, not ad id — a re-added ad always gets a fresh
 * id, so the id can never match. Each size holds a stack, so deleting two ads
 * of the same size and adding it back restores the most recently deleted one.
 *
 * A snapshot only describes per-ad overrides, so what invalidates it is the
 * layer STRUCTURE changing — a layer added (the snapshot has nothing to say
 * about it), removed, reordered, or re-parented. Global VALUE changes (a
 * fill, a font, a shape's path) do not: overrides are partial and resolve on
 * top of whatever the globals now are, exactly as they do for every ad that
 * was never deleted. `editorStore.structureRevision` counts the structural
 * ones; a snapshot taken at a different revision is stale and gets dropped in
 * favour of a fresh layout. Per-ad edits on other ads don't count either —
 * they can't affect this ad's overrides.
 *
 * Session-only: never serialized to the server, and cleared whenever the
 * editor resets or a different template loads. It does travel to sibling
 * browser tabs, both per-action and through the join handshake.
 */
import type { AdId, LayerId, LayerOverrides } from '@/types/layers/layer';
import type { AdLayoutSnapshot, SerializedAdRestoreCache } from '@/types/editor/adRestoreCache';

/**
 * How many deleted ads to remember per size. Restores read from the newest
 * end, so anything this far back is unreachable in practice — the cap just
 * stops a long editing session from accumulating layouts nobody will ask for,
 * which also ride along in the cross-tab handshake payload.
 */
const MAX_SNAPSHOTS_PER_SIZE = 10;

/** `"1080x1920"` — the identity a restore is matched on. */
function sizeKey(width: number, height: number): string {
  return `${width}x${height}`;
}

const snapshotsBySize = new Map<string, AdLayoutSnapshot[]>();

/**
 * Remember a deleted ad's layout. Called with the same override map the
 * DELETE_AD action carries for undo, captured before the store mutates.
 */
export function rememberDeletedAd(
  width: number,
  height: number,
  snapshot: AdLayoutSnapshot
): void {
  const key = sizeKey(width, height);
  const stack = snapshotsBySize.get(key);
  if (!stack) {
    snapshotsBySize.set(key, [snapshot]);
    return;
  }

  stack.push(snapshot);
  // Over the cap, drop from the front — the oldest deletes, furthest from the
  // newest-first end that restores read.
  if (stack.length > MAX_SNAPSHOTS_PER_SIZE) {
    stack.splice(0, stack.length - MAX_SNAPSHOTS_PER_SIZE);
  }
}

/**
 * Take back the most recent snapshot for this size, or null when there is
 * none or the layer structure has changed since. Always removes the entry it
 * inspects: a stale snapshot can only get staler, and a consumed one must not
 * be reused by the next ad of the same size.
 */
export function takeAdSnapshot(
  width: number,
  height: number,
  structureRevision: number
): AdLayoutSnapshot | null {
  const key = sizeKey(width, height);
  const stack = snapshotsBySize.get(key);
  if (!stack || stack.length === 0) return null;

  const snapshot = stack.pop()!;
  if (stack.length === 0) snapshotsBySize.delete(key);

  return snapshot.structureRevision === structureRevision ? snapshot : null;
}

/**
 * Drop one ad's snapshot without using it — for when undo brings that ad
 * back, so a later add of the same size doesn't restore a duplicate of an ad
 * that is already on the canvas.
 *
 * Matched on `adId` rather than just taking the newest: an add of the same
 * size in between will have consumed a later entry, so the top of the stack
 * is not reliably the ad being restored, and popping blind would throw away
 * a different ad's layout.
 */
export function forgetDeletedAd(
  width: number,
  height: number,
  adId: AdId
): void {
  const key = sizeKey(width, height);
  const stack = snapshotsBySize.get(key);
  if (!stack) return;

  // Newest first: on the (unexpected) chance an id repeats, drop the latest.
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i]!.adId === adId) {
      stack.splice(i, 1);
      break;
    }
  }
  if (stack.length === 0) snapshotsBySize.delete(key);
}

/** Wipe every snapshot — on editor reset or when another template loads. */
export function clearAdRestoreCache(): void {
  snapshotsBySize.clear();
}

/**
 * The cache as plain, structured-clone-safe data, for handing to a sibling
 * browser tab during the cross-tab join handshake. A tab that opens after a
 * delete never saw that DELETE_AD action, so this is its only way to learn
 * about the ad.
 */
export function serializeAdRestoreCache(): SerializedAdRestoreCache {
  return [...snapshotsBySize.entries()];
}

/**
 * Replace the cache with a sibling tab's, received via the handshake. The cap
 * is re-applied on the way in — the sending tab may be running an older build
 * with a different one.
 */
export function loadAdRestoreCache(entries: SerializedAdRestoreCache): void {
  snapshotsBySize.clear();
  for (const [key, stack] of entries) {
    if (stack.length > 0) {
      snapshotsBySize.set(key, stack.slice(-MAX_SNAPSHOTS_PER_SIZE));
    }
  }
}

/** Build the per-layer override snapshot for one ad from the layer map. */
export function captureAdOverrides(
  layers: Record<LayerId, { overrides: Record<AdId, LayerOverrides> }>,
  adId: AdId
): Record<LayerId, LayerOverrides> {
  const layerOverrides: Record<LayerId, LayerOverrides> = {};
  for (const [layerId, layer] of Object.entries(layers)) {
    if (layer.overrides[adId]) {
      layerOverrides[layerId] = { ...layer.overrides[adId] };
    }
  }
  return layerOverrides;
}
